import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getProductRatings } from "@/lib/reviews.functions";
import heroImg from "@/assets/hero-poster.jpg";
import heroPosterWebpSrcSet from "@/assets/hero-poster.jpg?w=640;768;1024;1280;1440;1600;1920&format=webp&as=srcset";
import { CATEGORY_TILE_SIZES } from "@/lib/image-srcset";
import { HeroVideo } from "@/components/site/HeroVideo";

import editorialJacket from "@/assets/product-jacket-black.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialJacketSrc from "@/assets/product-jacket-black.jpg?w=640&format=jpg&quality=72";
import editorialPurse from "@/assets/model-purse-cognac.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialPurseSrc from "@/assets/model-purse-cognac.jpg?w=640&format=jpg&quality=72";
import editorialWallet from "@/assets/product-wallet-cognac.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialWalletSrc from "@/assets/product-wallet-cognac.jpg?w=640&format=jpg&quality=72";
import editorialBeltDetail from "@/assets/model-belt-detail.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialBeltDetailSrc from "@/assets/model-belt-detail.jpg?w=640&format=jpg&quality=72";
import editorialBelt from "@/assets/product-belt-cognac.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialBeltSrc from "@/assets/product-belt-cognac.jpg?w=640&format=jpg&quality=72";
import editorialTote from "@/assets/product-purse-tote.jpg?w=400;640;900&format=webp&quality=70&as=srcset";
import editorialToteSrc from "@/assets/product-purse-tote.jpg?w=640&format=jpg&quality=72";
import { fetchProducts } from "@/lib/products";
import { LazyOnView } from "@/components/site/LazyOnView";
import { BenefitsStrip } from "@/components/site/BenefitsStrip";
import { RatedProductCard } from "@/components/site/RatedProductCard";
import { TwinBanners } from "@/components/site/TwinBanners";
import { InstagramGrid } from "@/components/site/InstagramGrid";
import { REVIEWS } from "@/lib/reviews";
import { faqPageJsonLd, speakableJsonLd, aboutPageJsonLd, SITE_URL } from "@/lib/seo";
import { Star } from "lucide-react";

const HOME_FAQ = [
  {
    question: "What is KAKAIYS?",
    answer:
      "KAKAIYS is a Real Leather house based in Sialkot, Pakistan. We craft jackets, bags, wallets, belts and small leather goods using full-grain and vegetable-tanned leather. Each piece is machine-constructed for strength and finished by hand — key seams, edges and details are stitched by our artisans.",
  },
  {
    question: "What kind of leather does KAKAIYS use?",
    answer:
      "Only Real Leather — primarily full-grain and top-grain hides including vegetable-tanned, aniline, nappa and lambskin — selected for grain integrity, hand-feel and how it ages. We never use bonded, corrected-grain, or synthetic leather.",
  },
  {
    question: "Are KAKAIYS pieces fully handmade?",
    answer:
      "We are honest: not every stitch is by hand. Construction uses precision industrial machines for durability. Hand-work is reserved for the parts that matter — saddle-stitched edges, hand-set hardware, hand-painted edge finishing, hand-lined collars and hand-burnished detailing.",
  },
  {
    question: "How should I care for a KAKAIYS leather jacket?",
    answer:
      "Wipe weekly with a dry cloth, condition every 3–6 months with a pH-neutral leather conditioner, store on a wide wooden hanger, and keep away from direct sunlight, radiators and damp.",
  },
  {
    question: "How can I tell real leather from synthetic?",
    answer:
      "Real leather has irregular grain, smells faintly sweet and earthy, warms to the touch, and develops a patina with use. Synthetic leather has uniform grain, smells of plastic, stays cool, and cracks rather than ages.",
  },
  {
    question: "Does KAKAIYS ship worldwide?",
    answer: "Yes — KAKAIYS ships internationally. Visit our shipping page for delivery times, duties and tracking.",
  },
];

const CATEGORY_TILES = [
  { label: "Jackets", to: "/men", img: editorialJacketSrc, webp: editorialJacket },
  { label: "Bags", to: "/women", img: editorialPurseSrc, webp: editorialPurse },
  { label: "Wallets", to: "/accessories", img: editorialWalletSrc, webp: editorialWallet },
  { label: "Belts", to: "/accessories", img: editorialBeltSrc, webp: editorialBelt },
  { label: "Riders", to: "/riders", img: editorialBeltDetailSrc, webp: editorialBeltDetail },
  { label: "Accessories", to: "/accessories", img: editorialToteSrc, webp: editorialTote },
] as const;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "KAKAIYS — Handcrafted Luxury Leather Jackets & Bags" },
      {
        name: "description",
        content:
          "Handcrafted full-grain leather jackets, bags, wallets and belts — finished by hand in Sialkot. Free worldwide shipping.",
      },
      { property: "og:title", content: "KAKAIYS — Handcrafted Leather. Built for Generations." },
      {
        property: "og:description",
        content: "Full-grain hides, finished by hand. Free worldwide shipping. Genuine leather, guaranteed.",
      },
      { property: "og:url", content: "https://kakaiys.com/" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: heroImg },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://kakaiys.com/" },
      {
        rel: "preload",
        as: "image",
        type: "image/webp",
        imageSrcSet: heroPosterWebpSrcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      } as unknown as { rel: string; href: string },
    ],
    scripts: [
      { type: "application/ld+json", children: faqPageJsonLd(HOME_FAQ) },
      { type: "application/ld+json", children: speakableJsonLd([".speakable-summary"]) },
      {
        type: "application/ld+json",
        children: aboutPageJsonLd({
          name: "KAKAIYS — Maison of Leather",
          description:
            "KAKAIYS is a hand-finished leather maison in Sialkot, Pakistan. Jackets, bags, wallets, belts and accessories made one at a time.",
          url: SITE_URL,
          topics: [
            "Leather jackets",
            "Full-grain leather",
            "Hand-stitched leather goods",
            "Heritage leather craft",
            "Sialkot leather",
          ],
        }),
      },
    ],
  }),
  loader: ({ context: { queryClient } }) => {
    void queryClient.prefetchQuery({
      queryKey: ["products", "active"],
      queryFn: () => fetchProducts(),
    });
  },
  component: Index,
});

function Index() {
  const { data: products = [] } = useQuery({
    queryKey: ["products", "active"],
    queryFn: () => fetchProducts(),
  });

  const bestSellers = products.slice(0, 8);
  const newArrivals = products
    .filter((p) => p.tags?.includes("new-arrivals"))
    .slice(0, 4);
  const newArrivalsFinal = newArrivals.length > 0 ? newArrivals : products.slice(0, 4);

  // Real review aggregates — only render rating row when count > 0.
  const visibleSlugs = useMemo(
    () =>
      Array.from(
        new Set([...bestSellers, ...newArrivalsFinal].map((p) => p.slug)),
      ),
    [bestSellers, newArrivalsFinal],
  );
  const { data: ratings = {} } = useQuery({
    queryKey: ["product-ratings", visibleSlugs],
    queryFn: () => getProductRatings({ data: { slugs: visibleSlugs } }),
    enabled: visibleSlugs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      {/* 1 — CINEMATIC HERO (untouched) */}
      <HeroVideo />

      {/* 2 — BENEFITS STRIP */}
      <BenefitsStrip />

      {/* 3 — OUR MOST LOVED JACKETS */}
      {bestSellers.length > 0 && (
        <section className="bg-cream">
          <div className="container-luxe py-24 md:py-32">
            <div className="text-center mb-16 md:mb-20 max-w-2xl mx-auto">
              <div className="eyebrow mb-5 text-gold">Best Sellers</div>
              <p className="mt-6 text-sm md:text-base text-ash leading-relaxed font-light">
                Chosen by clients for their leather quality, fit confidence and
                everyday wearability.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 lg:gap-x-10 gap-y-16">
              {bestSellers.map((p, i) => (
                <RatedProductCard key={p.slug} product={p} index={i} eager={i < 2} rating={ratings[p.slug]} />
              ))}
            </div>

            <div className="mt-20 text-center">
              <Link
                to="/shop"
                preload="intent"
                className="inline-block text-[11px] uppercase tracking-[0.32em] text-ink underline-grow"
              >
                View All Best Sellers
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 4 — SHOP BY CATEGORY */}
      <section className="bg-bone">
        <div className="container-luxe py-24 md:py-32">
          <div className="text-center mb-16 md:mb-20">
            <div className="eyebrow mb-5">The Universe</div>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              Shop by category.
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 md:gap-x-8 gap-y-12">
            {CATEGORY_TILES.map((c) => (
              <Link
                key={c.label}
                to={c.to}
                preload="intent"
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden img-frame bg-secondary">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={c.webp}
                      sizes={CATEGORY_TILE_SIZES}
                    />
                    <img
                      src={c.img}
                      alt={c.label}
                      loading="lazy"
                      decoding="async"
                      width={900}
                      height={1125}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 flex items-baseline justify-between text-bone">
                    <h3 className="font-serif text-xl md:text-2xl font-light text-bone drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
                      {c.label}
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.32em] text-bone opacity-95 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                      Shop
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — TWIN PROMOTIONAL BANNERS */}
      <TwinBanners />

      {/* 6 — NEW ARRIVALS */}
      {newArrivalsFinal.length > 0 && (
        <section className="bg-cream">
          <div className="container-luxe py-24 md:py-32">
            <div className="grid md:grid-cols-[1fr_2fr] gap-12 md:gap-16 items-start">
              <div className="md:sticky md:top-28">
                <div className="eyebrow mb-5 text-gold">New Arrivals</div>
                <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                  Fresh from the atelier.
                </h2>
                <p className="mt-6 text-sm md:text-base text-ash leading-relaxed font-light max-w-sm">
                  We appreciate the beauty of natural texture. Explore our newest
                  handcrafted leather pieces.
                </p>
                <Link
                  to="/new-arrivals"
                  preload="intent"
                  className="mt-10 inline-block text-[11px] uppercase tracking-[0.32em] text-ink underline-grow"
                >
                  Explore Now
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-x-5 md:gap-x-8 gap-y-14">
                {newArrivalsFinal.map((p, i) => (
                  <RatedProductCard key={p.slug} product={p} index={i} rating={ratings[p.slug]} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 7 — TESTIMONIALS */}
      <LazyOnView minHeight="700px">
        <section className="bg-bone">
          <div className="container-luxe py-24 md:py-32">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="eyebrow mb-5 text-gold">Since 2015</div>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
                Real clients. Real leather.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-10 md:gap-14 max-w-6xl mx-auto">
              {REVIEWS.slice(0, 3).map((r) => (
                <article
                  key={r.name}
                  className="text-center md:text-left flex flex-col gap-5"
                >
                  <div className="flex items-center justify-center md:justify-start gap-1">
                    {Array.from({ length: r.rating }).map((_, k) => (
                      <Star key={k} className="h-3 w-3 fill-gold text-gold" />
                    ))}
                  </div>
                  <blockquote className="font-serif font-light text-xl md:text-[1.4rem] leading-[1.5] text-ink italic">
                    “{r.body.split(". ").slice(0, 2).join(". ")}.”
                  </blockquote>
                  <div className="mt-auto">
                    <div className="font-serif text-base text-ink">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-stone mt-1">
                      {r.city}
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-16 text-center">
              <Link
                to="/reviews"
                preload="intent"
                className="inline-block text-[11px] uppercase tracking-[0.32em] text-ink underline-grow"
              >
                Read All Reviews
              </Link>
            </div>
          </div>
        </section>
      </LazyOnView>

      {/* 8 — ON THE GRAM */}
      <LazyOnView minHeight="600px">
        <InstagramGrid />
      </LazyOnView>

      {/* 9 — NEWSLETTER */}
      <LazyOnView minHeight="500px">
        <section className="bg-cream">
          <div className="container-luxe py-24 md:py-32">
            <div className="max-w-2xl mx-auto text-center">
              <div className="eyebrow mb-5">Private Correspondence</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                Join the maison.
              </h2>
              <p className="mt-6 text-base text-ash leading-relaxed font-light">
                A quiet monthly letter — new pieces, private events, and the
                craft behind the work. Reserved for our clients.
              </p>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="mt-12 flex max-w-md mx-auto border-b border-ink/30"
              >
                <label htmlFor="newsletter-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent px-1 py-3 text-sm focus:outline-none text-ink placeholder:text-stone"
                />
                <button
                  type="submit"
                  className="text-[11px] uppercase tracking-[0.32em] px-2 py-3 text-ink hover:text-stone transition-colors"
                >
                  Join
                </button>
              </form>
            </div>
          </div>
        </section>
      </LazyOnView>

      {/* AEO / LLMO — concise, AI-citable answers about the maison */}
      <section
        className="bg-bone py-24 md:py-32 border-t border-border"
        aria-labelledby="aeo-heading"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="eyebrow mb-6 text-center">Frequently Asked</div>
          <h2
            id="aeo-heading"
            className="font-serif font-light text-3xl md:text-5xl text-center text-ink mb-16 tracking-[-0.01em]"
          >
            About the maison.
          </h2>

          <div className="space-y-12 speakable-summary">
            {HOME_FAQ.map((q) => (
              <article key={q.question}>
                <h3 className="font-serif text-xl md:text-2xl text-ink mb-3 font-light">
                  {q.question}
                </h3>
                <p className="text-sm md:text-base text-ash leading-[1.85] font-light">
                  {q.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
